import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Search, Sparkles } from "lucide-react";

import { cn } from "../../lib/utils";
import {
  CHAT_PROMPT_CUSTOM_TYPE,
  filterPromptsForContext,
  isPromptUsable,
} from "../constants/chatConstants";
import type { PromptPickerListProps } from "../types/chat.types";

const PromptPickerList: React.FC<PromptPickerListProps> = ({
  prompts,
  recordType,
  query: externalQuery,
  showSearch = false,
  onSelect,
  hasCallRecordingWithTranscript,
  highlightedIndex = -1,
  onHighlightChange,
  listId = "prompt-picker-list",
  className,
}) => {
  const { t } = useTranslation();
  const [internalQuery, setInternalQuery] = useState("");
  const query = externalQuery ?? internalQuery;

  const filtered = useMemo(() => {
    const base = filterPromptsForContext(prompts, { recordType });
    if (!query.trim()) return base;
    const q = query.trim().toLowerCase();
    return base.filter(
      (p) =>
        p.name.toLowerCase().includes(q) ||
        p.description.toLowerCase().includes(q) ||
        p.promptType.toLowerCase().includes(q)
    );
  }, [prompts, recordType, query]);

  return (
    <div className={cn("flex flex-col", className)}>
      {showSearch ? (
        <div className="border-b border-gray-100 p-2">
          <div className="flex items-center gap-2 rounded-md bg-gray-50 px-2 py-1.5">
            <Search className="h-3.5 w-3.5 text-gray-400" />
            <input
              autoFocus
              value={internalQuery}
              onChange={(e) => setInternalQuery(e.target.value)}
              placeholder={t("Search prompts…") as string}
              className="w-full bg-transparent text-sm outline-none placeholder:text-gray-400"
              aria-label={t("Search prompts") as string}
            />
          </div>
        </div>
      ) : null}
      <div
        id={listId}
        role="listbox"
        aria-label={t("Prompt catalog") as string}
        aria-activedescendant={
          highlightedIndex >= 0 && filtered[highlightedIndex]
            ? `${listId}-option-${filtered[highlightedIndex].promptType}`
            : undefined
        }
        className="max-h-60 overflow-y-auto py-1"
      >
        {filtered.length === 0 ? (
          <p className="px-3 py-4 text-center text-xs text-gray-500">{t("No matches")}</p>
        ) : (
          filtered.map((p, index) => {
            const { usable, reason } = isPromptUsable(p, recordType, {
              hasCallRecordingWithTranscript,
            });
            const isCustom = p.promptType === CHAT_PROMPT_CUSTOM_TYPE;
            const isHighlighted = index === highlightedIndex;
            const optionId = `${listId}-option-${p.promptType}`;
            return (
              <button
                key={p.promptType}
                id={optionId}
                type="button"
                role="option"
                aria-selected={isHighlighted}
                onMouseEnter={() => onHighlightChange?.(index)}
                onClick={() => {
                  if (!usable) return;
                  onSelect(p);
                }}
                disabled={!usable}
                className={cn(
                  "flex w-full items-start gap-2 px-3 py-2 text-left text-sm transition-colors",
                  usable ? "hover:bg-gray-50" : "cursor-not-allowed text-gray-400",
                  isHighlighted && usable && "bg-gray-50"
                )}
                title={!usable ? reason : undefined}
              >
                <span
                  className={cn(
                    "mt-0.5 inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-md",
                    isCustom ? "bg-blue-50 text-blue-600" : "bg-gray-100 text-gray-600"
                  )}
                >
                  <Sparkles className="h-3 w-3" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-medium text-gray-900">{p.name}</span>
                  <span className="block truncate text-xs text-gray-500">{p.description}</span>
                </span>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
};

export default PromptPickerList;
